/**
 * Corporate Program Builder — Portfolio Structuring
 * Route: /corporate/programs
 *
 * Aggregates multiple initiatives into a single structured climate program.
 * Creates one investable deal ticket from a fragmented initiative portfolio.
 *
 * Flow:
 *   1. Select Initiatives
 *   2. Define Program
 *   3. Aggregation & Outputs
 *   4. Readiness
 *   5. Submit to FI Pipeline
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import client from "@/api/client";
import { toast } from "sonner";
import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
import {
  Layers, CheckCircle2, ArrowRight, RefreshCw, Plus, DollarSign,
  TrendingDown, Target, Zap, ChevronRight, Info, AlertCircle,
  BarChart2, Shield, FileText, Check
} from "lucide-react";

const CAT_LABELS = {
  renewable_energy: "Renewable Energy", energy_efficiency: "Energy Efficiency",
  fuel_switch: "Fuel Switching", waste_reduction: "Waste Reduction",
  process_optimisation: "Process Optimisation", materials_substitution: "Materials",
  low_carbon_materials: "Low-Carbon Materials",
  housing_low_carbon: "Low-Carbon Housing (DPCS-H v1.0)",
  other: "Other",
};
const READINESS_CONFIG = {
  strong:   { label: "Strong",   color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200",  dot: "bg-emerald-500"  },
  moderate: { label: "Moderate", color: "text-amber-700",   bg: "bg-amber-50 border-amber-200",      dot: "bg-amber-500"    },
  weak:     { label: "Weak",     color: "text-red-600",     bg: "bg-red-50 border-red-200",           dot: "bg-red-400"      },
};

// ── Initiative Selection Card ──────────────────────────────────────────────
function InitiativeSelectCard({ ini, isSelected, onToggle }) {
  const cat = CAT_LABELS[ini.category] || "Other";
  return (
    <div
      onClick={() => onToggle(ini.id)}
      className={`rounded-xl border-2 p-4 cursor-pointer transition-all ${
        isSelected ? "border-teal-400 bg-teal-50/30" : "border-slate-200 hover:border-teal-200 bg-white"
      }`}
      data-testid={`select-ini-${ini.id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
          isSelected ? "bg-teal-500 border-teal-500" : "border-slate-300"
        }`}>
          {isSelected && <Check size={10} className="text-white" strokeWidth={3} />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-slate-900 truncate">{ini.name}</p>
          <p className="text-xs text-slate-400">{cat} · {ini.status?.replace(/_/g," ")}</p>
        </div>
        <div className="text-right flex-shrink-0">
          {ini.expected_reduction_tco2e > 0 && <p className="text-xs font-bold text-teal-600">{ini.expected_reduction_tco2e}t/yr</p>}
          {ini.capex_usd > 0 && <p className="text-xs text-slate-400">${(ini.capex_usd/1000).toFixed(0)}k</p>}
        </div>
      </div>
    </div>
  );
}

// ── Program Summary Card ──────────────────────────────────────────────────
function ProgramSummaryCard({ program }) {
  const ready = READINESS_CONFIG[program.structuring_readiness] || READINESS_CONFIG.weak;
  return (
    <div className="bg-[#0F172A] rounded-xl p-6 text-white" data-testid="program-summary">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Structured Climate Program</p>
          <h2 className="text-lg font-bold text-white">{program.name}</h2>
          <p className="text-xs text-slate-400 mt-0.5">{program.ref_id} · {program.sector} · {program.geography}</p>
        </div>
        <div className={`text-xs font-bold px-3 py-1.5 rounded-full border ${ready.bg} ${ready.color}`}>
          {ready.label}
        </div>
      </div>

      {/* Aggregated metrics */}
      <div className="grid grid-cols-4 gap-4 mb-4">
        {[
          { label: "Annual Reduction",    value: `${program.total_reduction_tco2e}t`, color: "text-teal-700"   },
          { label: "Lifetime Total",      value: `${program.lifetime_reduction_tco2e}t`, color: "text-slate-900"   },
          { label: "Total CAPEX",         value: `$${(program.total_capex_usd/1000000).toFixed(1)}M`, color: "text-slate-900" },
          { label: "Carbon Value/yr",     value: program.vcm_value_low > 0 ? `$${(program.vcm_value_low/1000).toFixed(0)}k+` : "—", color: "text-emerald-700" },
        ].map(m => (
          <MetricCard key={m.label} label={m.label} value={m.value} color={m.color} />
        ))}
      </div>

      {/* Units context for housing programs */}
      {program.units_count > 0 && (
        <div className="bg-teal-900/30 border border-teal-700/30 rounded-xl px-4 py-2.5 mb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Layers size={12} className="text-teal-400" />
              <span className="text-xs text-teal-300 font-bold">Housing Programme — {program.units_count.toLocaleString()} Units</span>
            </div>
            <div className="flex items-center gap-4 text-xs text-slate-400">
              <span>Per unit: {program.per_unit_reduction_tco2e?.toFixed(3)} tCO₂e/yr</span>
              <span>Per unit CAPEX: ${program.per_unit_capex_usd?.toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}

      {/* Initiative count + cost/t */}
      <div className="flex items-center gap-4 text-xs text-slate-400 pt-3 border-t border-white/8">
        <span>{program.initiative_ids?.length || 0} initiatives bundled</span>
        {program.cost_per_tco2e && <span>Cost/tCO₂e: ${program.cost_per_tco2e}</span>}
        <span>{program.duration_years} year program</span>
      </div>
    </div>
  );
}

// ── MAIN PAGE ──────────────────────────────────────────────────────────────
export default function CorporateProgramBuilder() {
  const navigate           = useNavigate();
  const { currentOrg }     = useAuth();
  const orgId              = currentOrg?.id;

  const [step,           setStep]           = useState(1);   // 1=select, 2=define, 3=review, 4=submit
  const [initiatives,    setInitiatives]    = useState([]);
  const [programs,       setPrograms]       = useState([]);
  const [selectedIds,    setSelectedIds]    = useState(new Set());
  const [form,           setForm]           = useState({ name: "", geography: "", sector: "", duration_years: 10, description: "", units_count: null });
  const [program,        setProgram]        = useState(null);   // created program
  const [submitting,     setSubmitting]     = useState(false);
  const [creating,       setCreating]       = useState(false);
  const [submitted,      setSubmitted]      = useState(null);
  const [loading,        setLoading]        = useState(true);

  // Detect if any selected initiative is housing type
  const selectedList    = initiatives.filter(i => selectedIds.has(i.id));
  const isHousingProgram = selectedList.some(i => i.category === "housing_low_carbon");
  const totalR  = selectedList.reduce((s, i) => s + (i.expected_reduction_tco2e || 0), 0);
  const totalC  = selectedList.reduce((s, i) => s + (i.capex_usd || 0), 0);
  // For housing: per-unit vs scaled
  const unitsN  = isHousingProgram ? (parseInt(form.units_count) || 1) : null;
  const scaledR = unitsN ? totalR * unitsN : totalR;
  const scaledC = unitsN ? totalC * unitsN : totalC;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [initRes, progRes] = await Promise.all([
        orgId ? client.get(`/corporate/orgs/${orgId}/initiatives`) : Promise.resolve({ data: [] }),
        client.get("/programs/"),
      ]);
      setInitiatives(initRes.data || []);
      setPrograms(progRes.data?.programs || []);
    } catch {}
    setLoading(false);
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  // Auto-populate form from org
  useEffect(() => {
    if (currentOrg && !form.geography) {
      setForm(f => ({
        ...f,
        geography: currentOrg.country || "",
        sector:    currentOrg.sector   || "",
      }));
    }
  }, [currentOrg]);

  const handleToggle = (id) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else if (n.size < 8) n.add(id);
      return n;
    });
  };

  const handleCreateProgram = async () => {
    if (selectedIds.size < 2) { toast.warning("Select at least 2 initiatives to structure a program."); return; }
    if (!form.name.trim())    { toast.warning("Enter a program name."); return; }
    if (isHousingProgram && !form.units_count) { toast.warning("Enter the number of housing units."); return; }
    setCreating(true);
    try {
      const res = await client.post("/programs/", {
        name:           form.name,
        initiative_ids: Array.from(selectedIds),
        geography:      form.geography,
        sector:         form.sector,
        duration_years: parseInt(form.duration_years) || 10,
        description:    form.description,
        units_count:    unitsN,
        program_type:   isHousingProgram ? "housing_low_carbon" : "general",
      });
      setProgram(res.data);
      setStep(3);
      toast.success(`Program structured: ${res.data.ref_id}${unitsN ? ` · ${unitsN.toLocaleString()} units` : ""}`);
      await load();
    } catch { toast.error("Program creation failed."); }
    setCreating(false);
  };

  const handleSubmitToFI = async () => {
    if (!program) return;
    setSubmitting(true);
    try {
      const res = await client.post(`/programs/${program.id}/submit-to-fi`);
      setSubmitted(res.data);
      setStep(4);
      toast.success(res.data.message);
    } catch { toast.error("Submission failed."); }
    setSubmitting(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <RefreshCw size={18} className="animate-spin text-teal-500" />
    </div>
  );

  return (
    <div className="p-6 lg:p-8 min-h-screen" data-testid="program-builder">
      {/* Header */}
      <div className="flex items-start justify-between mb-7">
            <Layers size={18} className="text-teal-600" />
            
      <PageHeader
        label="Selection Summary"
        title="Portfolio Structuring"
        backUrl="/corporate/workspace"
        backLabel="Back to Workspace"
      />
          <p className="text-sm text-slate-400">
            Aggregate multiple initiatives into a single structured climate program —
            one deal ticket, larger capital, greater DFI interest.
          </p>
        </div>
        <button onClick={() => navigate("/corporate/workspace")}
          className="text-xs text-slate-400 hover:text-slate-600">
          ← Back to Workspace
        </button>

      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-7">
        {[
          { n: 1, label: "Select Initiatives" },
          { n: 2, label: "Define Program"     },
          { n: 3, label: "Review & Structure" },
          { n: 4, label: "FI Submission"       },
        ].map((s, i) => (
          <React.Fragment key={s.n}>
            <div className={`flex items-center gap-1.5 text-xs font-bold ${
              step === s.n ? "text-teal-600" : step > s.n ? "text-emerald-600" : "text-slate-300"
            }`}>
              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                step > s.n ? "bg-emerald-500 text-white" : step === s.n ? "bg-teal-600 text-white" : "bg-slate-200 text-slate-400"
              }`}>
                {step > s.n ? <Check size={10} strokeWidth={3} /> : s.n}
              </div>
              <span className="hidden sm:block">{s.label}</span>
            </div>
            {i < 3 && <div className={`flex-1 h-px ${step > s.n ? "bg-emerald-300" : "bg-slate-200"}`} />}
          </React.Fragment>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main canvas */}
        <div className="lg:col-span-2 space-y-5">

          {/* STEP 1: Select Initiatives */}
          {step === 1 && (
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h2 className="text-sm font-bold text-slate-900 mb-1">Step 1: Select Initiatives to Bundle</h2>
              <p className="text-xs text-slate-400 mb-4">
                Choose 2–8 related initiatives to aggregate into one program. Group initiatives with compatible
                methodologies and geographies for maximum credibility.
              </p>
              {initiatives.length === 0 ? (
                <div className="text-center py-8">
                  <Layers size={24} className="text-slate-200 mx-auto mb-2" />
                  <p className="text-xs text-slate-400 mb-3">No initiatives yet.</p>
                  <button onClick={() => navigate("/corporate/initiatives")}
                    className="flex items-center gap-2 mx-auto px-4 py-2 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700">
                    <Plus size={12} /> Add Initiatives
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {initiatives.map(i => (
                    <InitiativeSelectCard key={i.id} ini={i} isSelected={selectedIds.has(i.id)} onToggle={handleToggle} />
                  ))}
                </div>
              )}
              {selectedIds.size >= 2 && (
                <button onClick={() => setStep(2)}
                  className="mt-4 flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700"
                  data-testid="next-to-define-btn">
                  Continue — Define Program <ArrowRight size={14} />
                </button>
              )}
            </div>
          )}

          {/* STEP 2: Define Program */}
          {step === 2 && (
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <button onClick={() => setStep(1)} className="text-slate-400 hover:text-slate-600 text-xs">← Back</button>
                <h2 className="text-sm font-bold text-slate-900">Step 2: Define the Program</h2>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1.5">Program Name</label>
                  <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                    placeholder="e.g. Sahara Cement Industrial Decarbonization Program 2024–2034"
                    className="w-full h-11 px-4 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-teal-400"
                    data-testid="program-name-input" />
                </div>

                {/* Units scaling — only for housing programs */}
                {isHousingProgram && (
                  <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Layers size={13} className="text-teal-600" />
                      <p className="text-xs font-bold text-teal-800">Housing Programme Scale</p>
                      <span className="text-xs text-teal-600 bg-teal-100 px-1.5 py-0.5 rounded-full font-bold">DPCS-H v1.0</span>
                    </div>
                    <p className="text-xs text-teal-700 mb-3">
                      Per-unit model detected. Set the total number of units to scale the programme metrics.
                      Total reduction = per-unit reduction × N units.
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-slate-500 font-semibold uppercase tracking-wider mb-1">Number of Units</label>
                        <input type="number" min="1" value={form.units_count || ""}
                          onChange={e => setForm(f => ({...f, units_count: e.target.value}))}
                          placeholder="e.g. 500"
                          className="w-full h-10 px-3 border border-teal-300 rounded-lg text-sm focus:outline-none focus:border-teal-500 bg-white font-mono font-bold"
                          data-testid="program-units-input" />
                        <p className="text-xs text-slate-400 mt-1">Supports any number (10, 100, 1,000, 10,000+)</p>
                      </div>
                      {unitsN > 0 && (
                        <div className="bg-white rounded-xl p-3 border border-teal-200">
                          <p className="text-xs text-slate-500 mb-1.5">Programme totals at {unitsN.toLocaleString()} units:</p>
                          <p className="text-sm font-bold text-teal-700">{scaledR.toFixed(1)}t CO₂e/yr</p>
                          <p className="text-xs text-slate-400">${(scaledC/1000000).toFixed(2)}M CAPEX</p>
                          <p className="text-xs text-slate-400">vs per-unit: {totalR.toFixed(3)}t / ${(totalC/1000).toFixed(0)}k</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1.5">Geography / Countries</label>
                    <input value={form.geography} onChange={e => setForm(f => ({...f, geography: e.target.value}))}
                      placeholder="e.g. Nigeria, Ghana"
                      className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-400" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1.5">Program Duration (years)</label>
                    <input type="number" value={form.duration_years} onChange={e => setForm(f => ({...f, duration_years: e.target.value}))}
                      className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-400 font-mono"
                      data-testid="program-duration-input" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1.5">Program Description (optional)</label>
                  <textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))}
                    placeholder="Describe the overarching program objective, expected outcomes, and connection between initiatives..."
                    rows={3} className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm resize-none focus:outline-none focus:border-teal-400" />
                </div>
              </div>
              <button onClick={handleCreateProgram} disabled={creating || !form.name.trim()}
                className="mt-5 flex items-center gap-2 px-6 py-3 bg-[#0F172A] text-white text-sm font-bold rounded-xl hover:bg-slate-800 disabled:opacity-40"
                data-testid="create-program-btn">
                {creating ? <RefreshCw size={13} className="animate-spin" /> : <Zap size={13} />}
                {creating ? "Structuring program..." : "Structure Program"}
              </button>
            </div>
          )}

          {/* STEP 3: Review & Readiness */}
          {step === 3 && program && (
            <div className="space-y-4">
              <ProgramSummaryCard program={program} />

              {/* MRV Approach */}
              <SectionCard>

                <h3 className="text-sm font-bold text-slate-900 mb-3">Unified MRV Approach</h3>
                <p className="text-xs text-slate-600 leading-relaxed">{program.mrv_approach}</p>
                        </SectionCard>

              {/* Bundled initiatives */}
              <SectionCard>

                <h3 className="text-sm font-bold text-slate-900 mb-3">
                  Bundled Initiatives ({program.initiative_data?.length || 0})
                </h3>
                <div className="space-y-2">
                  {(program.initiative_data || []).map((ini, i) => (
                    <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-lg px-4 py-2.5">
                      <CheckCircle2 size={13} className="text-emerald-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-slate-900 truncate">{ini.name}</p>
                        <p className="text-xs text-slate-400">{ini.category}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs font-bold text-teal-600">{ini.reduction}t/yr</p>
                        <p className="text-xs text-slate-400">${(ini.capex/1000).toFixed(0)}k</p>
                      </div>
                    </div>
                  ))}
                </div>
                        </SectionCard>

              {/* Readiness */}
              <div className={`rounded-xl border p-5 ${READINESS_CONFIG[program.structuring_readiness]?.bg || "bg-slate-50 border-slate-200"}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${READINESS_CONFIG[program.structuring_readiness]?.dot}`} />
                  <p className={`text-sm font-bold ${READINESS_CONFIG[program.structuring_readiness]?.color}`}>
                    Structuring Readiness: {READINESS_CONFIG[program.structuring_readiness]?.label}
                  </p>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">{program.readiness_notes}</p>
              </div>

              {/* Disclaimer */}
              <div className="flex items-start gap-2 text-xs text-slate-400">
                <Info size={11} className="flex-shrink-0 mt-0.5" />
                <p>
                  This program structure is indicative for planning and financing purposes. Carbon market pathway suitability
                  is subject to independent additionality assessment, methodology validation, and host country authorization.
                  DeCarbonIQ does not certify carbon credits or issue sovereign authorizations.
                </p>
              </div>

              <button onClick={() => setStep(4)} data-testid="next-to-submit-btn"
                className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700">
                Continue to FI Submission <ArrowRight size={14} />
              </button>
            </div>
          )}

          {/* STEP 4: FI Submission */}
          {step === 4 && program && !submitted && (
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h2 className="text-sm font-bold text-slate-900 mb-1">Step 4: Submit to FI Pipeline</h2>
              <p className="text-xs text-slate-400 mb-5">
                Submit this structured program as a single large deal to the FI financing queue.
                The entire program — {program.initiative_data?.length} initiatives, {program.total_reduction_tco2e}t CO₂e/yr,
                ${(program.total_capex_usd/1000000).toFixed(1)}M CAPEX — appears as one deal.
              </p>

              {/* Program value summary */}
              <div className="bg-[#0F172A] rounded-xl p-4 mb-5 text-white">
                <p className="text-xs text-slate-400 uppercase tracking-wide mb-2">Program to be submitted</p>
                <p className="text-base font-bold text-white">{program.name}</p>
                <p className="text-xs text-slate-400 mt-0.5">{program.ref_id} · {program.initiative_ids?.length} initiatives aggregated</p>
                <div className="grid grid-cols-3 gap-4 mt-3">
                  <div><p className="text-lg font-bold text-teal-400">{program.total_reduction_tco2e}t</p><p className="text-xs text-slate-400">CO₂e/yr</p></div>
                  <div><p className="text-lg font-bold text-white">${(program.total_capex_usd/1000000).toFixed(1)}M</p><p className="text-xs text-slate-400">CAPEX</p></div>
                  <div><p className="text-lg font-bold text-emerald-400">${(program.vcm_value_low/1000).toFixed(0)}k+</p><p className="text-xs text-slate-400">Carbon value/yr</p></div>
                </div>
              </div>

              <button onClick={handleSubmitToFI} disabled={submitting}
                className="w-full h-14 bg-[#0F172A] text-white text-sm font-bold rounded-xl hover:bg-slate-800 disabled:opacity-40 flex items-center justify-center gap-2"
                data-testid="submit-program-to-fi-btn">
                {submitting ? <><RefreshCw size={14} className="animate-spin" /> Submitting...</> : <><DollarSign size={14} /> Submit Program to FI Pipeline</>}
              </button>
            </div>
          )}

          {/* STEP 4: Success */}
          {step === 4 && submitted && (
            <div className="bg-white border border-emerald-200 rounded-xl p-8 text-center" data-testid="program-submitted">
              <CheckCircle2 size={40} className="text-emerald-500 mx-auto mb-3" />
              <p className="text-xl font-bold text-slate-900 mb-1">Program Submitted</p>
              <p className="text-sm text-slate-400 mb-1">Reference: <strong className="font-mono">{submitted.request_id}</strong></p>
              <p className="text-xs text-slate-400 mb-6">{submitted.message}</p>
              <div className="flex gap-3 justify-center flex-wrap">
                <button onClick={() => navigate("/fi/requests")}
                  className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700">
                  View in FI Queue <ArrowRight size={14} />
                </button>
                <button onClick={() => { setStep(1); setSelectedIds(new Set()); setProgram(null); setSubmitted(null); }}
                  className="text-xs text-slate-400 underline">
                  Structure another program
                </button>
              </div>
            </div>
          )}

          {/* Existing programs */}
          {programs.length > 0 && step === 1 && (
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="text-sm font-bold text-slate-900 mb-3">Existing Programs</h3>
              <div className="space-y-2">
                {programs.map(p => {
                  const ready = READINESS_CONFIG[p.structuring_readiness] || READINESS_CONFIG.weak;
                  return (
                    <div key={p.id} className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="text-xs font-bold text-slate-900 truncate">{p.name}</p>
                          <span className="text-xs font-mono text-slate-400">{p.ref_id}</span>
                        </div>
                        <p className="text-xs text-slate-400">
                          {p.initiative_ids?.length} initiatives · {p.total_reduction_tco2e}t/yr · {p.status}
                        </p>
                      </div>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${ready.bg} ${ready.color}`}>
                        {ready.label}
                      </span>
                      {p.fi_request_id && (
                        <span className="text-xs text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full font-bold">
                          Submitted
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Right: Selection summary */}
        <div className="space-y-4">
          {/* Live selection summary */}
          {step <= 2 && (
            <div className="bg-[#0F172A] rounded-xl p-5 text-white sticky top-20">
              <p className="text-xs text-slate-400 uppercase tracking-widest font-bold mb-3">Selection Summary</p>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-slate-400">Selected</p>
                  <p className="text-2xl font-bold text-white">{selectedIds.size} <span className="text-sm font-normal text-slate-400">/ max 8</span></p>
                </div>
                {totalR > 0 && (
                  <div>
                    <p className="text-xs text-slate-400">Combined Reduction</p>
                    <p className="text-xl font-bold text-teal-400">{Math.round(totalR)}t <span className="text-xs font-normal text-slate-400">CO₂e/yr</span></p>
                  </div>
                )}
                {totalC > 0 && (
                  <div>
                    <p className="text-xs text-slate-400">Total CAPEX</p>
                    <p className="text-xl font-bold text-white">${(totalC/1000000).toFixed(1)}M</p>
                  </div>
                )}
                {totalR > 0 && totalC > 0 && (
                  <div className="pt-2 border-t border-white/8">
                    <p className="text-xs text-slate-400">Indicative Carbon Value</p>
                    <p className="text-base font-bold text-emerald-400">
                      ${(totalR*15/1000).toFixed(0)}k–${(totalR*30/1000).toFixed(0)}k/yr
                    </p>
                  </div>
                )}
              </div>

              {selectedIds.size < 2 && (
                <p className="text-xs text-amber-400 mt-3">Select at least 2 initiatives to structure a program</p>
              )}
            </div>
          )}

          {/* What is a program */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-xs font-bold text-slate-900 mb-2">Why bundle into a program?</p>
            <div className="space-y-2">
              {[
                "One deal ticket — larger financing target",
                "Stronger MRV framework across all sites",
                "Greater DFI and institutional interest",
                "More credible as a carbon market asset",
              ].map(item => (
                <div key={item} className="flex items-start gap-2 text-xs text-slate-500">
                  <ArrowRight size={10} className="text-teal-500 flex-shrink-0 mt-0.5" />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
